import React, { useEffect, useRef, useCallback, memo } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Confetti from 'react-confetti';
import useAnimationSequence from '../hooks/useAnimationSequence';
import { screenShake } from '../styles/animations';

// Styled components for the animation controller
const ControllerContainer = styled.div`
  position: fixed;
  bottom: ${({ theme }) => theme.space.lg};
  right: ${({ theme }) => theme.space.lg};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.sm};
  z-index: 10;
`;

const ControlButton = styled.button`
  background: rgba(0, 0, 0, 0.7);
  border: 1px solid ${({ theme }) => theme.colors.primary};
  padding: ${({ theme }) => theme.space.sm} ${({ theme }) => theme.space.md};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ProgressBar = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  height: 4px;
  background: ${({ theme }) => theme.colors.primary};
  z-index: 100;
  width: ${({ progress }) => `${progress}%`};
  transition: width 0.3s linear;
`;

const ScreenShakeContainer = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1000;
`;

/**
 * AnimationController Component
 * Controls the step-by-step reveal animation sequence
 * Provides UI controls for starting, resetting, and skipping animations
 * Optimized for performance and to prevent memory leaks
 *
 * @param {Object} props - Component props
 * @param {Function} props.onAnimationStateChange - Callback for animation state changes
 * @param {boolean} props.autoStart - Whether to start animation automatically
 */
const AnimationController = ({
  onAnimationStateChange,
  autoStart = false
}) => {
  const {
    showFourthPlace,
    showThirdPlace,
    showSecondPlace,
    showFirstPlace,
    showCelebration,
    isComplete,
    isAnimating,
    startAnimation,
    resetAnimation,
    skipToEnd,
    totalDuration
  } = useAnimationSequence();
  
  // Calculate animation progress percentage
  const [progress, setProgress] = React.useState(0);
  const [elapsedTime, setElapsedTime] = React.useState(0);
  const [screenShakeActive, setScreenShakeActive] = React.useState(false);
  
  // Use ref for interval to prevent memory leaks
  const progressIntervalRef = useRef(null);
  const screenShakeTimeoutRef = useRef(null);
  
  // Cleanup function to clear all timeouts and intervals
  const cleanupTimers = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    
    if (screenShakeTimeoutRef.current) {
      clearTimeout(screenShakeTimeoutRef.current);
      screenShakeTimeoutRef.current = null;
    }
  }, []);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => cleanupTimers();
  }, [cleanupTimers]);
  
  // Start animation automatically if autoStart is true
  useEffect(() => {
    if (autoStart) {
      startAnimation();
    }
  }, [autoStart, startAnimation]);
  
  // Update animation state in parent component
  useEffect(() => {
    if (onAnimationStateChange) {
      onAnimationStateChange({
        showFourthPlace,
        showThirdPlace,
        showSecondPlace,
        showFirstPlace,
        showCelebration,
        isComplete,
        isAnimating
      });
    }
    
    // Trigger screen shake when first place is revealed
    if (showFirstPlace && !screenShakeActive) {
      setScreenShakeActive(true);
      
      // Clear any existing timeout
      if (screenShakeTimeoutRef.current) {
        clearTimeout(screenShakeTimeoutRef.current);
      }
      
      // Set new timeout and store the ID
      screenShakeTimeoutRef.current = setTimeout(() => {
        setScreenShakeActive(false);
        screenShakeTimeoutRef.current = null;
      }, 500);
    }
  }, [
    showFourthPlace,
    showThirdPlace,
    showSecondPlace,
    showFirstPlace,
    showCelebration,
    isComplete,
    isAnimating,
    onAnimationStateChange,
    screenShakeActive
  ]);
  
  // Update progress bar with optimized interval (250ms instead of 100ms)
  useEffect(() => {
    // Clear any existing interval
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    
    if (!isAnimating) {
      return;
    }
    
    // Create new interval and store the ID
    progressIntervalRef.current = setInterval(() => {
      setElapsedTime(prev => {
        const newElapsed = prev + 250; // Increased from 100ms to 250ms
        const newProgress = Math.min((newElapsed / totalDuration) * 100, 100);
        setProgress(newProgress);
        
        if (newProgress >= 100) {
          // Clear interval when progress is complete
          if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
          }
        }
        
        return newElapsed;
      });
    }, 250); // Increased from 100ms to 250ms for better performance
    
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
  }, [isAnimating, totalDuration]);
  
  // Reset progress when animation is reset
  useEffect(() => {
    if (!isAnimating) {
      setElapsedTime(0);
      setProgress(isComplete ? 100 : 0);
    }
  }, [isAnimating, isComplete]);
  
  return (
    <>
      {/* Progress bar */}
      <ProgressBar progress={progress} />
      
      {/* Screen shake effect for 1st place reveal */}
      <ScreenShakeContainer
        variants={screenShake}
        initial="hidden"
        animate={screenShakeActive ? "visible" : "hidden"}
      />
      
      {/* Confetti celebration for 1st place - reduced number of pieces */}
      {showCelebration && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none', // This ensures clicks pass through to elements below
          zIndex: 5 // Lower than controls but higher than background
        }}>
          <Confetti
            width={window.innerWidth}
            height={window.innerHeight}
            recycle={false}
            numberOfPieces={200} // Reduced from 500 to 200
            gravity={0.2}
            colors={['#FFD700', '#00D4FF', '#8B5CF6']} // Reduced color variety
          />
        </div>
      )}
      
      {/* Animation controls */}
      <ControllerContainer>
        {!isAnimating && !isComplete && (
          <ControlButton onClick={startAnimation}>
            Start Reveal
          </ControlButton>
        )}
        
        {isAnimating && (
          <ControlButton onClick={skipToEnd}>
            Skip to End
          </ControlButton>
        )}
        
        {isComplete && (
          <ControlButton onClick={resetAnimation}>
            Reset
          </ControlButton>
        )}
        
        {isComplete && (
          <ControlButton onClick={startAnimation}>
            Replay
          </ControlButton>
        )}
      </ControllerContainer>
    </>
  );
};

// Memoize the component to prevent unnecessary re-renders
export default memo(AnimationController);